import * as Sentry from "@sentry/react";

// True for a machine-local host. Sentry is not sent from these (see `enabled` below).
export function isLocalHostname(host: string): boolean {
  const h = host.toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.localhost');
}

export function initSentry() {
  const environment = process.env.NODE_ENV || 'development';
  const domain = process.env.REACT_APP_SENTRY_DOMAIN || window.location.origin;
  const isLocal = isLocalHostname(window.location.hostname);

  console.log('Initializing Sentry with:', { environment, domain });

  Sentry.init({
    dsn: "https://bf4896fedab3c94a54c7d5844964cad0@o4510166485237760.ingest.de.sentry.io/4510166486483024",

    // No events, traces or replays from localhost, so local and agent sessions do not send
    // PII to the production project. Opt in with REACT_APP_SENTRY_LOCAL=true. Deployed hosts are unaffected.
    enabled: !isLocal || process.env.REACT_APP_SENTRY_LOCAL === 'true',

    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of transactions for performance monitoring

    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Environment
    environment,

    // Set tags that will be applied to all events
    initialScope: {
      tags: {
        domain,
        app_version: process.env.REACT_APP_VERSION || '1.0.0',
      },
    },

    // Add additional context to events
    beforeSend(event, hint) {
      // Log to console for debugging (remove in production if needed)
      console.log('Sentry event being sent:', event.event_id, event.level);

      // Add custom context
      event.tags = {
        ...event.tags,
        domain,
      };

      event.contexts = {
        ...event.contexts,
        app: {
          ...event.contexts?.app,
          domain,
          origin: window.location.origin,
        },
      };

      return event;
    },
  });

  // Set user context if needed
  Sentry.setContext("deployment", {
    domain,
    environment,
    timestamp: new Date().toISOString(),
  });

  console.log('Sentry initialized successfully');
}
